import { useState } from 'react';
import './Calculators.css';

// Simplified MIPI (Hoster 2008) — 4 factors scored 0-3 (ECOG 0 or 2), total 0-11
// Risk groups: Low 0-3, Intermediate 4-5, High 6-11 (matches the bands used by MIPIcCalculator)
const AGE_POINTS = [
  { value: 0, label: 'Age < 50' },
  { value: 1, label: 'Age 50-59' },
  { value: 2, label: 'Age 60-69' },
  { value: 3, label: 'Age ≥ 70' },
];

const ECOG_POINTS = [
  { value: 0, label: 'ECOG 0-1' },
  { value: 2, label: 'ECOG 2-4' },
];

const LDH_POINTS = [
  { value: 0, label: 'LDH / ULN < 0.67' },
  { value: 1, label: 'LDH / ULN 0.67-0.99' },
  { value: 2, label: 'LDH / ULN 1.00-1.49' },
  { value: 3, label: 'LDH / ULN ≥ 1.50' },
];

const WBC_POINTS = [
  { value: 0, label: 'WBC < 6.7 ×10⁹/L' },
  { value: 1, label: 'WBC 6.7-9.99 ×10⁹/L' },
  { value: 2, label: 'WBC 10.0-14.99 ×10⁹/L' },
  { value: 3, label: 'WBC ≥ 15.0 ×10⁹/L' },
];

const MIPI_RISK = [
  { range: [0, 3], group: 'Low', color: '#15803d', medianOS: 'Not reached', os5yr: '60%' },
  { range: [4, 5], group: 'Intermediate', color: '#a16207', medianOS: '51 months', os5yr: null },
  { range: [6, 11], group: 'High', color: '#dc2626', medianOS: '29 months', os5yr: null },
];

function getRiskGroup(score, table) {
  return table.find((r) => score >= r.range[0] && score <= r.range[1]);
}

function MIPICalculator() {
  const [age, setAge] = useState(0);
  const [ecog, setEcog] = useState(0);
  const [ldh, setLdh] = useState(0);
  const [wbc, setWbc] = useState(0);

  const score = age + ecog + ldh + wbc;
  const risk = getRiskGroup(score, MIPI_RISK);

  return (
    <div className="calculator">
      <div className="calc-header">
        <h3>Simplified MIPI</h3>
        <span className="calc-subtitle">Mantle Cell Lymphoma International Prognostic Index</span>
      </div>

      <div className="calc-factors">
        <div className="calc-factor-group">
          <div className="calc-factor-label-bold">Age (0-3 pts)</div>
          <select value={age} onChange={(e) => setAge(parseInt(e.target.value, 10))}>
            {AGE_POINTS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label} ({a.value} pt)
              </option>
            ))}
          </select>
        </div>

        <div className="calc-factor-group">
          <div className="calc-factor-label-bold">ECOG performance status (0 or 2 pts)</div>
          <select value={ecog} onChange={(e) => setEcog(parseInt(e.target.value, 10))}>
            {ECOG_POINTS.map((e2) => (
              <option key={e2.value} value={e2.value}>
                {e2.label} ({e2.value} pt)
              </option>
            ))}
          </select>
        </div>

        <div className="calc-factor-group">
          <div className="calc-factor-label-bold">LDH ratio to ULN (0-3 pts)</div>
          <select value={ldh} onChange={(e) => setLdh(parseInt(e.target.value, 10))}>
            {LDH_POINTS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label} ({l.value} pt)
              </option>
            ))}
          </select>
        </div>

        <div className="calc-factor-group">
          <div className="calc-factor-label-bold">Leukocyte count (0-3 pts)</div>
          <select value={wbc} onChange={(e) => setWbc(parseInt(e.target.value, 10))}>
            {WBC_POINTS.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label} ({w.value} pt)
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="calc-results">
        <div className="calc-score">
          <span className="calc-score-value">{score}</span>
          <span className="calc-score-label">/ 11 points</span>
        </div>

        <div className="calc-risk-row">
          <span className="calc-risk-label">Risk Group:</span>
          <span className="calc-risk-badge" style={{ background: risk.color }}>
            {risk.group}
          </span>
        </div>

        <div className="calc-risk-row">
          <span className="calc-risk-label">Median OS:</span>
          <span className="calc-risk-stat">{risk.medianOS}</span>
        </div>

        {risk.os5yr && (
          <div className="calc-risk-row">
            <span className="calc-risk-label">5-yr OS:</span>
            <span className="calc-risk-stat">{risk.os5yr}</span>
          </div>
        )}
      </div>

      <div className="calc-note" style={{ fontSize: '11px', color: '#6b7280', marginTop: '8px' }}>
        Published simplified MIPI: points for age, ECOG, LDH/ULN ratio and leukocyte count are summed (0-11 points). Low risk 0-3, intermediate 4-5, high 6-11. Hoster 2008 reports 5-year OS only for the low-risk group; the intermediate and high-risk groups are described by median OS. Derived in advanced-stage MCL treated with chemotherapy ± rituximab. Consider MIPI-c if Ki-67 is available.
      </div>

      <div className="calc-reference">
        Hoster E, Dreyling M, Klapper W, et al. A new prognostic index (MIPI) for patients with advanced-stage mantle cell lymphoma. <em>Blood.</em> 2008;111(2):558-565. <a href="https://pubmed.ncbi.nlm.nih.gov/17962512/" target="_blank" rel="noopener noreferrer">PMID 17962512</a>
      </div>
    </div>
  );
}

export default MIPICalculator;
